import { useCallback, useEffect, useRef, useState } from 'react';
import { EvolutionService } from '../services/EvolutionService';

export type FaseQr = 'parado' | 'nome' | 'criando' | 'gerando' | 'lendo' | 'conectado' | 'recriar' | 'erro' | 'expirado';

/* O /QRCode da Evolution Go reinicia a sessão e devolve o código: é consultado a
   cada 3 s até parear, cancelar ou estourar ~2 min. Mesmo ciclo que a tela antiga
   fazia (commit d8bde97). */
const INTERVALO_MS = 3_000;
const LIMITE_CONSULTAS = 40;

/* Apaga a conexão abandonada. Confere antes: o número pode ter pareado entre a
   última consulta (a cada 3 s) e o fechar, e aí ele fica. */
const descartar = async (id: number) => {
    try {
        const st = await EvolutionService.getStatus(id);
        if (st.sucesso && st.dados?.conectado) return;
        await EvolutionService.remover(id);
    } catch { /* fica Inativa; Conexões mostra e deixa remover */ }
};

interface Opcoes {
    /** Enquanto for verdadeiro o ciclo roda; ao virar falso, a conexão que este ciclo criou e não pareou é apagada. */
    ativo: boolean;
    /** Conexão já existente para reconectar. Sem ela, o ciclo espera um `criar(nome)`. */
    conexaoId?: number | null;
    /** O número leu o código e pareou. */
    onConectado: () => void;
}

/**
 * O miolo de "conectar WhatsApp via QR Code" (28/10/2026): saiu do painel de
 * Conexões para o guia de boas-vindas mostrar o código dentro do bloco, com o
 * mesmo ciclo — criar a conexão como Inativa, consultar o código até parear e
 * apagar a conexão se quem abriu desistir antes. Quem desenha é cada tela;
 * aqui só o que acontece.
 */
export function useConectarQr({ ativo, conexaoId = null, onConectado }: Opcoes) {
    const [fase, setFase] = useState<FaseQr>('parado');
    const [qr, setQr] = useState<string | null>(null);
    const [msg, setMsg] = useState('');
    const [idAtual, setIdAtual] = useState<number | null>(conexaoId);
    const cancelar = useRef<(() => void) | null>(null);
    const conectadoRef = useRef(onConectado);
    conectadoRef.current = onConectado;
    /* A conexão nasce Inativa. Se quem abriu desiste (fecha, troca de página)
       antes de parear, ela não pode ficar para trás: o status da empresa contaria
       um número que nunca conectou. Só se apaga a que ESTE ciclo criou — a de
       reconectar já existia antes. */
    const criadaAqui = useRef<number | null>(null);
    const pareou = useRef(false);
    /* Cada ativação ganha um token. A criação que volta depois de o ciclo parar
       (ou de ele ter sido reativado) não é desta ativação e é descartada. */
    const abertura = useRef<object | null>(null);

    const consultar = useCallback((id: number) => {
        cancelar.current?.();
        let parar = false;
        cancelar.current = () => { parar = true; };
        setQr(null); setFase('gerando'); setMsg('');

        (async () => {
            for (let i = 0; i < LIMITE_CONSULTAS && !parar; i++) {
                let r;
                try {
                    r = await EvolutionService.getQRCode(id);
                } catch {
                    if (!parar) { setFase('erro'); setMsg('Falha de conexão ao gerar o código.'); }
                    return;
                }
                if (parar) return;

                if (!r.sucesso) {
                    /* Instância sumiu na Evolution Go (apagada à mão, ou conexão da versão
                       anterior): a API pede para recriar — na mesma conexão, sem perder conversas. */
                    setFase(/recrie a inst/i.test(r.mensagem || '') ? 'recriar' : 'erro');
                    setMsg(r.mensagem || 'Não foi possível gerar o código.');
                    return;
                }
                if (r.dados?.conectado) {
                    pareou.current = true;
                    setFase('conectado');
                    conectadoRef.current();
                    return;
                }
                if (r.dados?.qrCodeBase64) {
                    const b = r.dados.qrCodeBase64;
                    setQr(b.startsWith('data:') ? b : `data:image/png;base64,${b}`);
                    setFase('lendo');
                }
                await new Promise(res => setTimeout(res, INTERVALO_MS));
            }
            if (!parar) { setFase('expirado'); setMsg('O tempo para leitura do código acabou. Gere um novo.'); }
        })();
    }, []);

    /* Ativar: reconectar já começa a consultar; conexão nova espera o nome. */
    useEffect(() => {
        if (!ativo) { setFase('parado'); return; }
        abertura.current = {};
        setQr(null); setMsg(''); setIdAtual(conexaoId);
        criadaAqui.current = null;
        pareou.current = false;
        if (conexaoId !== null) consultar(conexaoId);
        else setFase('nome');
        return () => {
            abertura.current = null;
            cancelar.current?.(); cancelar.current = null;
            if (criadaAqui.current !== null && !pareou.current) descartar(criadaAqui.current);
            criadaAqui.current = null;
        };
    }, [ativo, conexaoId, consultar]);

    /* O nome vira também o nome da instância na Evolution Go, que não muda depois. */
    const criar = useCallback(async (nome: string): Promise<boolean> => {
        const esta = abertura.current;
        if (!esta) return false;
        setFase('criando'); setMsg('');
        try {
            const r = await EvolutionService.criar({ nome });
            if (abertura.current !== esta) {
                // Parou enquanto criava: a conexão chegou depois do adeus.
                if (r.sucesso && r.dados?.conexaoPlataformaId) descartar(r.dados.conexaoPlataformaId);
                return false;
            }
            if (!r.sucesso || !r.dados?.conexaoPlataformaId) {
                setFase('nome'); setMsg(r.mensagem || 'Não foi possível criar a conexão.');
                return false;
            }
            const id = r.dados.conexaoPlataformaId;
            criadaAqui.current = id;
            setIdAtual(id);
            consultar(id);
            return true;
        } catch {
            if (abertura.current === esta) { setFase('nome'); setMsg('Erro ao criar a conexão.'); }
            return false;
        }
    }, [consultar]);

    const recriar = useCallback(async () => {
        if (idAtual === null) return;
        setFase('gerando'); setMsg('');
        try {
            const r = await EvolutionService.criar({ conexaoPlataformaId: idAtual });
            if (!r.sucesso) { setFase('erro'); setMsg(r.mensagem || 'Não foi possível recriar a instância.'); return; }
            consultar(idAtual);
        } catch {
            setFase('erro'); setMsg('Erro ao recriar a instância.');
        }
    }, [idAtual, consultar]);

    const tentarDeNovo = useCallback(() => { if (idAtual !== null) consultar(idAtual); }, [idAtual, consultar]);

    return {
        fase,
        qr,
        msg,
        idAtual,
        criar,
        recriar,
        tentarDeNovo,
        ocupado: fase === 'criando' || fase === 'gerando',
    };
}
